import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  getBusinessActivities, 
  getBusinessDetails,
  deleteActivity, 
  updateActivity,
  createActivity 
} from '@/utils/businessAppApi';
import { BusinessActivity, CreateActivityInput, UpdateActivityInput } from '@/types/businessAppTypes';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ActivityTabs } from './components/ActivityTabs';
import { CreateActivityModal } from './components/CreateActivityModal';
import { EditActivityModal } from './components/EditActivityModal';
import { ConfirmDeleteModal } from './components/ConfirmDeleteModal';

import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24
    }
  }
};

// Simple ErrorBoundary for debugging
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: any }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  componentDidCatch(error: any, info: any) {
    console.error('[ERROR BOUNDARY CAUGHT]', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
          <div className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white rounded-lg p-6 text-center max-w-lg">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 border border-amber-200/50 flex items-center justify-center mx-auto mb-4 shadow-sm">
              <svg className="w-6 h-6 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Something went wrong</h2>
            <p className="text-sm text-slate-600 mb-4">Please refresh the page or try again later.</p>
            <Button 
              variant="outline" 
              onClick={() => window.location.reload()}
              className="border-slate-200/80 shadow-sm hover:shadow-md transition-shadow"
            >
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function ActivityManagement() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [businessLocation, setBusinessLocation] = useState<[number, number]>([0, 0]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<BusinessActivity | null>(null);
  const [activities, setActivities] = useState<BusinessActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [businessImages, setBusinessImages] = useState<string[]>([]);

  const { toast } = useToast();
  const { t } = useTranslation('business', { keyPrefix: 'activities' });
  const { t: tc } = useTranslation('common');

  useEffect(() => {
    fetchBusinessDetails();
    fetchActivities();
  }, []);

  const fetchBusinessDetails = async () => {
    try {
      const response = await getBusinessDetails();
      const coordinates = response.data.location.coordinates.coordinates;
      setBusinessLocation(coordinates as [number, number]);
      
      // Extract business images from the response
      const images = response.data.images?.gallery || [];
      setBusinessImages(images);
    } catch (error) {
      console.error('Error fetching business details:', error);
      toast({
        title: tc('error'),
        description: t('failedToLoadBusinessDetails'),
        variant: "destructive",
      });
    }
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getBusinessActivities();
      
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format received from server');
      }
      
      setActivities(data);
      setRetryCount(0);
    } catch (error) {
      console.error('Error in fetchActivities:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load activities';
      setError(errorMessage);
      toast({
        title: tc('error'),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateActivity = async (activityData: CreateActivityInput) => {
    setIsCreating(true);
    try {
      const newActivity = await createActivity(activityData);
      // Optimistically add the new activity for immediate feedback
      setActivities(prevActivities => [newActivity, ...prevActivities]);
      setIsCreateModalOpen(false);
      toast({
        title: `${tc('success')}! 🎉`,
        description: t('activityCreated'),
        duration: 5000,
      });
      // Refetch activities from backend to ensure consistency
      await fetchActivities();
    } catch (error) {
      console.error('Error creating activity:', error);
      toast({
        title: tc('error'),
        description: t('failedToCreate'),
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteActivity = (id: string) => {
    setActivityToDelete(id);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDeleteActivity = async () => {
    if (activityToDelete) {
      try {
        await deleteActivity(activityToDelete);
        setActivities(activities.filter(activity => activity._id !== activityToDelete));
        toast({
          title: tc('success'),
          description: t('activityDeleted'),
        });
      } catch (error) {
        console.error('Error deleting activity:', error);
        toast({
          title: tc('error'),
          description: t('failedToDelete'),
          variant: "destructive",
        });
      } finally {
        setIsConfirmDeleteOpen(false);
        setActivityToDelete(null);
      }
    }
  };

  const handleSaveActivity = async (updatedActivity: Partial<BusinessActivity>) => {
    try {
      const updateData: UpdateActivityInput = {
        id: updatedActivity._id!,
        name: updatedActivity.name,
        description: updatedActivity.description,
        activityType: updatedActivity.activityType,
        type: updatedActivity.type as 'business' | 'free' | 'community',
        location: {
          type: "Point",
          coordinates: updatedActivity.location?.coordinates as number[],
        },
        pricing: updatedActivity.pricing,
        maxParticipants: updatedActivity.maxParticipants,
        isActive: updatedActivity.isActive,
        images: updatedActivity.images
      };

      const updated = await updateActivity(updateData);
      setActivities(activities.map(activity => 
        activity._id === updated._id ? updated : activity
      ));
      setIsEditModalOpen(false);
      toast({
        title: tc('success'),
        description: t('activityUpdated'),
      });
    } catch (error) {
      console.error('Error updating activity:', error);
      toast({
        title: tc('error'),
        description: t('failedToUpdate'),
        variant: "destructive",
      });
    }
  };

  const handleEditActivity = (activity: BusinessActivity) => {
    setSelectedActivity(activity);
    setIsEditModalOpen(true);
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex items-center justify-center min-h-[60vh]"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Loader2 className="h-5 w-5 animate-spin text-white" />
            </div>
            <span className="text-lg font-medium text-slate-900">{t('loadingActivities')}</span>
          </div>
          <p className="text-sm text-slate-600">{t('waitWhileFetch')}</p>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex flex-col items-center justify-center min-h-[60vh] p-6"
      >
        <div className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white rounded-lg p-6 max-w-md w-full text-center">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 border border-amber-200/50 flex items-center justify-center mx-auto mb-4 shadow-sm">
            <svg className="w-6 h-6 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">{t('unableToLoad')}</h3>
          <p className="text-slate-600 mb-4 text-sm">{error}</p>
          <div className="flex flex-col gap-3">
            <Button 
              onClick={() => {
                setRetryCount(prev => prev + 1);
                fetchActivities();
              }}
              className="w-full bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 shadow-md hover:shadow-lg transition-shadow"
            >
              {retryCount > 0 ? `${t('retry')} (${retryCount})` : t('retry')}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => window.location.reload()}
              className="w-full border-slate-200/80 shadow-sm hover:shadow-md transition-shadow"
            >
              {t('refreshPage')}
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <ErrorBoundary>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6"
      >
        {/* Clean Header Section - Matching BookingManagement Style */}
        <motion.div variants={itemVariants}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900">{t('activityManagement')}</h1>
              <p className="text-gray-600 mt-1">
                {t('activityManagementSubtitle')}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Statistics Cards - Matching BookingManagement Style */}
        <motion.div variants={itemVariants}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{activities.length}</div>
                    <div className="text-sm text-gray-600">{t('totalActivities')}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {activities.filter(a => a.isActive).length}
                    </div>
                    <div className="text-sm text-gray-600">{t('activeStatus')}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {activities.reduce((sum, activity) => sum + (activity.maxParticipants || 0), 0)}
                    </div>
                    <div className="text-sm text-gray-600">{t('totalCapacity')}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      ${activities.filter(a => a.pricing?.defaultPrices?.adult?.amount).length > 0 
                        ? Math.round(activities.reduce((sum, a) => sum + (a.pricing?.defaultPrices?.adult?.amount || 0), 0) / activities.filter(a => a.pricing?.defaultPrices?.adult?.amount).length) 
                        : 0}
                    </div>
                    <div className="text-sm text-gray-600">{t('averagePrice')}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>

        {/* Main Content - Activities Table */}
        <motion.div variants={itemVariants} className="space-y-4">
          <Tabs defaultValue="activities" className="space-y-4">

            <TabsContent value="activities">
              <AnimatePresence mode="wait">
                {activities.length === 0 ? (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ type: "spring", stiffness: 300, damping: 24 }}
                    className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white rounded-lg overflow-hidden"
                  >
                    <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-200/50 flex items-center justify-center mb-6 shadow-sm">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">{t('noActivitiesFound')}</h3>
                      <p className="text-slate-600 mb-6 max-w-md">{t('noActivitiesDesc')}</p>
                      <Button 
                        onClick={() => setIsCreateModalOpen(true)}
                        className="bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 px-6 shadow-md hover:shadow-lg transition-shadow font-medium"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        {t('createFirstActivity')}
                      </Button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ActivityTabs
                      activities={activities}
                      onEditActivity={handleEditActivity}
                      onDeleteActivity={handleDeleteActivity}
                      onCreateActivity={() => setIsCreateModalOpen(true)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </TabsContent>

          </Tabs>
        </motion.div>

        <CreateActivityModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateActivity}
          businessLocation={businessLocation}
          isLoading={isCreating}
          businessImages={businessImages}
        />

        <EditActivityModal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          activity={selectedActivity}
          onSave={handleSaveActivity}
          businessImages={businessImages}
        />

        <ConfirmDeleteModal
          open={isConfirmDeleteOpen}
          onClose={() => setIsConfirmDeleteOpen(false)}
          onConfirm={confirmDeleteActivity}
        />
      </motion.div>
    </ErrorBoundary>
  );
} 